// The Research Center (SKnow; DECISIONS "Research levels", "Research Center build choices"). Route /knowledge (the
// old name; "Knowledge base" is the folders). The numbers line (verified, new this week, waiting to verify, followed),
// Today's reading over what is New; Insights, Following (links the team watches) and Research (tasks at a level, the
// runs, the topics). Insights (6.3): filters that combine and count (category or New, confidence, tag, source; in the
// URL, so a filtered view is a link; Clear), as cards (New first) or a table (Finding, Confidence, Tags, Verified by,
// Date; newest first, a header sorts). Every kept insight says "Verified by <name> · <date>"; the rest "Unverified".
// An insight opens in a sheet: summary, key points, details, each quote with its check, why it matters, Keep or
// Dismiss for what the assistant found (a failed check asks once: "Keep anyway"), Save to a folder. Partners see kept
// only. Violet only for what the assistant made (its New badge, its reading); buttons stay black or white.
import { AddButton } from '../ui/AddButton';
import { useAiScope } from '../assistant/AiButton';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  ApiError, CONFIDENCE_LABELS, FOLLOW_FREQUENCIES, FREQUENCY_LABELS, INSIGHT_CATEGORIES, LEVEL_INFO, RESEARCH_LEVELS, can,
  INSIGHT_CONFIDENCE, type ContestedPair, type Follow, type FollowFrequency, type Insight, type InsightCategory, type InsightConfidence, type ResearchLevel,
  type ResearchOverview, type ResearchTask,
} from '@dpw/shared';
import { api } from '../api';
import { useMe } from '../lib/queries';
import { ago } from '../lib/format';
import { usePhone } from '../lib/media';
import { listItem, spring } from '../lib/motion';
import { useToast } from '../ui/toast';
import { Sheet } from '../ui/Sheet';
import { Modal } from '../ui/Modal';
import { ErrorNote, Loading } from '../ui/bits';
import { Orb, SkelList } from '../ui/motion';
import { PhoneResearch } from '../phone/Research';
import { useAssistantPanel } from '../assistant/Panel';
import { playAudio, stopAudio, unlockAudio } from '../voice/audio';
import {
  applyFilter, facets, insightDate, isFiltered, nextSort, NO_FILTER, readFilter, readSort, sortInsights, verifiedBy, writeFilter,
  type InsightFilter, type SortKey, type TableSort,
} from '../research/filters';

type Tab = 'ins' | 'fol' | 'res';
const KEYS = ['research', 'insights', 'insight', 'research-contested', 'research-tasks', 'research-task', 'follows', 'follow', 'research-runs', 'research-new'];

/** After any change: everything the Research Center shows. */
export function useResearchChange<A, R>(fn: (a: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSettled: () => Promise.all(KEYS.map(k => qc.invalidateQueries({ queryKey: [k] }))) });
}

export const levelTag = (l: ResearchLevel) => <span className={`lvt ${l}`}><i aria-hidden="true">{[1, 2, 3].map(n => <u key={n} className={n <= (l === 'quick' ? 1 : l === 'standard' ? 2 : 3) ? 'on' : ''} />)}</i>{LEVEL_INFO[l].name}</span>;

export function KnowledgePage() {
  useAiScope({ purpose: 'research', label: 'Research Center' });
  return usePhone() ? <PhoneResearch /> : <DesktopResearch />;
}

function DesktopResearch() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'ins';
  /** Change the query in one go (several keys at once would otherwise overwrite each other). */
  const setQ = (fn: (p: URLSearchParams) => void, replace = true) => setParams(p => { fn(p); return p; }, { replace });
  const setTab = (t: Tab) => setQ(p => p.set('tab', t));
  const me = useMe().data;
  const team = !!me && can(me.role, 'activity.write');
  const ov = useQuery({ queryKey: ['research'], queryFn: api.research.overview, refetchInterval: 30_000 });
  const filter = readFilter(params);
  const setFilter = (f: Partial<InsightFilter>) => setQ(p => { writeFilter(p, f); });
  const ins = useQuery({ queryKey: ['insights'], queryFn: () => api.research.insights(), refetchInterval: 30_000 });
  const [adding, setAdding] = useState(false);
  const panel = useAssistantPanel();
  // Add an insight: the assistant panel helps fill it in; "Fill it in myself" opens the plain form.
  const addInsight = () => panel.open({ purpose: 'insight', fresh: true, manual: { label: 'Fill it in myself', run: () => setAdding(true) } });
  /** The new-task sheet, open with the question it starts from ('' for none). */
  const [tasking, setTasking] = useState<string | null>(null);
  const open = params.get('insight');
  const openTask = params.get('task');
  const openFollow = params.get('follow');
  const close = (k: string) => setQ(p => p.delete(k));
  const show = (k: string, v: string) => setQ(p => p.set(k, v), false);
  const view = params.get('view') === 'table' ? 'table' : 'list';
  const sort = readSort(params);

  const all = ins.data ?? [];
  const vis = applyFilter(all, filter);
  const counts = ov.data?.counts;
  const tabs: [Tab, string, string][] = [
    ['ins', 'Insights', counts?.newInsights ? `${counts.newInsights} new` : ''], ['fol', 'Following', String(counts?.following ?? '')], ['res', 'Research', `${counts?.openTasks ?? 0} open`],
  ];
  return (
    <div className="rc col" style={{ gap: 16 }}>
      <div className="hdr">
        <div className="htl"><h1>Research Center</h1><span className="xs mut">Outside intelligence on AI in healthcare and healthcare strategy · shared by the team</span></div>
        {team && <div className="hact">
          <AddButton onClick={addInsight}>Add an insight</AddButton><AddButton className="rtask" onClick={() => setTasking('')}>New research task</AddButton>
        </div>}
      </div>
      {counts && <ResearchStats counts={counts} team={team} onWaiting={() => setQ(p => { p.set('tab', 'ins'); writeFilter(p, { ...NO_FILTER, cat: 'New' }); })} />}
      <div className="kbbar">
        <div className="seg2" role="tablist" aria-label="Research Center">
          {tabs.map(([k, l, n]) => <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{l} <span className="mono xs">{n}</span></button>)}
        </div>
      </div>
      {ov.data?.reading && team && (
        <section className="reading" aria-label="Today's reading">
          <div className="rh"><Orb size={14} /><b>Today&apos;s reading</b><span className="xs mut">What the {ov.data.reading.insightIds.length} new {ov.data.reading.insightIds.length === 1 ? 'insight means' : 'insights mean'} together</span>
            <span className="cd"><span className="xs mut">Next research run</span><b>{untilText(ov.data.nextRunAt)}</b></span></div>
          <div className="t">{ov.data.reading.text}</div>
          <div className="ra">
            <button type="button" className="bt sm" onClick={() => setQ(p => { p.set('tab', 'ins'); writeFilter(p, { ...NO_FILTER, cat: 'New' }); })}>See the {ov.data.reading.insightIds.length} new</button>
            <ListenButton text={ov.data.reading.text} />
            <button type="button" className="bt sm" onClick={() => { unlockAudio(); panel.open({ purpose: 'research', voice: true, fresh: true }); }}>Talk it through</button>
          </div>
        </section>
      )}
      {tab === 'ins' && <>
        {ov.data?.week && <div className="xs mut">Weekly briefing: {ov.data.week.text}</div>}
        <ContestedSection team={team} onOpen={id => show('insight', id)} />
        <InsightFilters all={all} shown={vis.length} filter={filter} team={team} onChange={setFilter} onClear={() => setFilter(NO_FILTER)}
          view={view} onView={v => setQ(p => { if (v === 'table') p.set('view', 'table'); else { p.delete('view'); p.delete('sort'); p.delete('dir'); } })} />
        {ins.isLoading ? <SkelList rows={4} /> : ins.error ? <ErrorNote error={ins.error} /> : view === 'table' ? (
          <InsightTable list={vis} sort={sort} onSort={k => setQ(p => { const n = nextSort(sort, k); p.set('sort', n.key); p.set('dir', n.dir); })} onOpen={id => show('insight', id)}
            empty={all.length ? 'No insights match these filters.' : 'Nothing here yet. Ask it to find some.'} />
        ) : (
          <div className="gems" role="list" aria-label="Insights">
            <AnimatePresence initial={false}>
              {vis.map(i => (
                <motion.button key={i.id} layout="position" variants={listItem} initial="initial" animate="animate" exit="exit" type="button" role="listitem"
                  className={`gem${i.status === 'new' ? ' new' : ''}`} onClick={() => show('insight', i.id)} data-insight={i.id}>
                  <div className="rowc btwc" style={{ flexWrap: 'nowrap' }}><span className="catl">{i.category}</span>{i.status === 'new' && <span className="newb">New</span>}</div>
                  <h3>{i.title}</h3>
                  <p>{i.summary}</p>
                  {i.checkFailed && i.status === 'new' && <span className="xs" style={{ color: 'var(--amb-tx)' }}>A quote did not check out</span>}
                  <div className="gtags">{confidencePill(i)}{i.tags.map(t => <span key={t} className="itg">#{t}</span>)}</div>
                  <div className="ft"><span>{i.sourceDomain ?? 'Added text'} · {ago(i.createdAt)}</span><span style={{ flex: 1 }} /><span className={`vby${i.status === 'kept' ? '' : ' un'}`}>{i.byLabel}</span></div>
                </motion.button>
              ))}
            </AnimatePresence>
            {!vis.length && <div className="panel sm mut">{all.length ? 'No insights match these filters.' : 'Nothing here yet. Ask it to find some.'}</div>}
          </div>
        )}
      </>}
      {tab === 'fol' && <FollowingTab team={team} onOpen={id => show('follow', id)} />}
      {tab === 'res' && <ResearchTab onOpen={id => show('task', id)} />}
      <AnimatePresence>
        {open && <InsightSheet key={open} id={open} team={team} onClose={() => close('insight')}
          onSource={d => setQ(p => { p.delete('insight'); p.set('tab', 'ins'); p.set('src', d); }, false)}
          onMore={title => { close('insight'); setTasking(`More on: ${title}`); }} />}
        {openTask && <TaskSheet key={openTask} id={openTask} onClose={() => close('task')} onInsight={id => setQ(p => { p.delete('task'); p.set('insight', id); }, false)} />}
        {openFollow && <FollowSheet key={openFollow} id={openFollow} team={team} onClose={() => close('follow')} onInsight={id => setQ(p => { p.delete('follow'); p.set('insight', id); }, false)} />}
        {adding && <Modal key="add" label="Add an insight" onClose={() => setAdding(false)}><InsightForm onCancel={() => setAdding(false)} onAdded={id => { setAdding(false); show('insight', id); }} /></Modal>}
        {tasking !== null && <NewTaskSheet key="task" initial={tasking} onClose={() => setTasking(null)} onStarted={id => { setTasking(null); setQ(p => { p.set('tab', 'res'); p.set('task', id); }, false); }} />}
      </AnimatePresence>
    </div>
  );
}

/** The numbers line: real counts only (the overview). Partners do not see what waits to be verified. */
function ResearchStats({ counts, team, onWaiting }: { counts: ResearchOverview['counts']; team: boolean; onWaiting: () => void }) {
  return (
    <div className="rstats" role="group" aria-label="Research Center in numbers">
      <div><span>Verified</span><b>{counts.verified}</b><i>{counts.verified === 1 ? 'insight' : 'insights'}</i></div>
      <div><span>This week</span><b>{counts.verifiedThisWeek}</b><i>newly verified</i></div>
      {team && <button type="button" onClick={onWaiting} aria-label={`Waiting to verify: ${counts.newInsights}. Show them`}><span>Waiting to verify</span><b>{counts.newInsights}</b><i>found by the assistant</i></button>}
      <div><span>Resources followed</span><b>{counts.following}</b><i>{counts.following === 1 ? 'link' : 'links'}</i></div>
    </div>
  );
}

const CONF_PILL = { confirmed: 'grn', single: 'neu', contested: 'amb' } as const;
const confidencePill = (i: Insight) => <span className={`pl ${CONF_PILL[i.confidence]}`} data-conf={i.confidence}>{CONFIDENCE_LABELS[i.confidence]}</span>;

/** How many tags show before "More tags". */
const TAGS_SHOWN = 12;

/**
 * The filters: category (or New) chips, confidence, tags and source, each with what it would leave; Clear when any is
 * on; List or Table on the right.
 */
function InsightFilters({ all, shown, filter, team, onChange, onClear, view, onView }: {
  all: Insight[]; shown: number; filter: InsightFilter; team: boolean; onChange: (f: Partial<InsightFilter>) => void; onClear: () => void;
  view: 'list' | 'table'; onView: (v: 'list' | 'table') => void;
}) {
  const f = facets(all, filter, { team });
  const [moreTags, setMoreTags] = useState(false);
  const tags = moreTags ? f.tags : f.tags.slice(0, TAGS_SHOWN);
  if (filter.tag && !tags.some(t => t.tag === filter.tag)) tags.push(f.tags.find(t => t.tag === filter.tag)!);
  return (
    <section className="rfilt" aria-label="Filters">
      <div className="rfrow">
        <div className="cats" role="group" aria-label="Category">
          {f.cats.map(c => (
            <button key={c.label} type="button" className={`cat${filter.cat === c.key ? ' on' : ''}`} aria-pressed={filter.cat === c.key} onClick={() => onChange({ cat: c.key })}>{c.label}<span className="n">{c.n}</span></button>
          ))}
        </div>
        <div className="seg2 rview" role="group" aria-label="View">
          {(['list', 'table'] as const).map(v => <button key={v} type="button" aria-pressed={view === v} className={view === v ? 'on' : ''} onClick={() => onView(v)}>{v === 'list' ? 'List' : 'Table'}</button>)}
        </div>
      </div>
      <div className="rfrow">
        {!!f.conf.length && <div className="rfg" role="group" aria-label="Confidence"><span className="rfl">Confidence</span>
          {f.conf.map(c => (
            <button key={c.key} type="button" className={`cat sm${filter.conf === c.key ? ' on' : ''}`} aria-pressed={filter.conf === c.key} onClick={() => onChange({ conf: filter.conf === c.key ? null : c.key })}>{c.label}<span className="n">{c.n}</span></button>
          ))}</div>}
        <label className="rfg"><span className="rfl">Source</span>
          <select className="inp sm" aria-label="Source" value={filter.src ?? ''} onChange={e => onChange({ src: e.target.value || null })}>
            <option value="">All sources</option>
            {f.sources.map(s => <option key={s.domain} value={s.domain}>{s.domain} ({s.n})</option>)}
          </select></label>
        <span style={{ flex: 1 }} />
        <span className="xs mut" role="status">{isFiltered(filter) ? `${shown} of ${all.length} ${all.length === 1 ? 'insight' : 'insights'}` : `${all.length} ${all.length === 1 ? 'insight' : 'insights'}`}</span>
        {isFiltered(filter) && <button type="button" className="bt sm" onClick={onClear}>Clear filters</button>}
      </div>
      <div className="rfg" role="group" aria-label="Tags"><span className="rfl">Tags</span>
        {tags.map(t => (
          <button key={t.tag} type="button" className={`cat sm tagc${filter.tag === t.tag ? ' on' : ''}`} aria-pressed={filter.tag === t.tag} onClick={() => onChange({ tag: filter.tag === t.tag ? null : t.tag })}>#{t.tag}<span className="n">{t.n}</span></button>
        ))}
        {f.tags.length > TAGS_SHOWN && <button type="button" className="bt sm" onClick={() => setMoreTags(m => !m)}>{moreTags ? 'Fewer tags' : `${f.tags.length - TAGS_SHOWN} more`}</button>}
        {!f.tags.length && <span className="xs mut">No tags yet. New insights get them from the assistant; Edit adds them by hand.</span>}
      </div>
    </section>
  );
}

const COLS: [SortKey, string][] = [['finding', 'Finding'], ['confidence', 'Confidence'], ['tags', 'Tags'], ['by', 'Verified by'], ['date', 'Date']];
const shortDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/** The table view: one row per insight; a header sorts by it (again: the other way round). */
function InsightTable({ list, sort, onSort, onOpen, empty }: { list: Insight[]; sort: TableSort; onSort: (k: SortKey) => void; onOpen: (id: string) => void; empty: string }) {
  const rows = sortInsights(list, sort);
  return (
    <div className="tblwrap panel" role="table" aria-label="Insights table">
      <div className="itr h" role="row">
        {COLS.map(([k, l]) => (
          <span key={k} role="columnheader" aria-sort={sort.key === k ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
            <button type="button" className={`ith${sort.key === k ? ' on' : ''}`} onClick={() => onSort(k)}>{l}{sort.key === k && <i aria-hidden="true">{sort.dir === 'asc' ? '↑' : '↓'}</i>}</button>
          </span>
        ))}
      </div>
      {rows.map(i => (
        <div className={`itr${i.status === 'new' ? ' new' : ''}`} role="row" key={i.id} data-insight={i.id} onClick={() => onOpen(i.id)}>
          <div role="cell" style={{ minWidth: 0 }}><button type="button" className="flink" onClick={e => { e.stopPropagation(); onOpen(i.id); }}>{i.title}</button>
            <div className="xs mut">{i.category}{i.status === 'new' && <> · <span className="newb">New</span></>}</div></div>
          <span role="cell">{confidencePill(i)}</span>
          <span role="cell" className="itags">{i.tags.length ? i.tags.map(t => <span key={t} className="itg">#{t}</span>) : <span className="xs mut">None</span>}</span>
          <span role="cell" className={`sm${i.status === 'kept' ? '' : ' mut'}`}>{verifiedBy(i)}</span>
          <span role="cell" className="xs mono mut">{shortDate(insightDate(i))}</span>
        </div>
      ))}
      {!rows.length && <div className="sm mut" style={{ padding: 16 }}>{empty}</div>}
    </div>
  );
}

/**
 * Contested: pairs of insights whose sources disagree, side by side (neither hidden), each with its claim, quote and
 * source. Resolve: "This one holds up" (the other is dismissed, after one confirmation), or keep both with a note.
 */
function ContestedSection({ team, onOpen }: { team: boolean; onOpen: (id: string) => void }) {
  const q = useQuery({ queryKey: ['research-contested'], queryFn: api.research.contested, refetchInterval: 30_000 });
  if (!q.data?.length) return null;
  return (
    <section className="panel ctd" aria-label="Contested">
      <div className="sh"><b>Contested</b><span className="mono xs">{q.data.length}</span>
        <span className="xs mut">Sources disagree. Both stay, side by side, until someone resolves it.</span></div>
      {q.data.map(p => <ContestedRow key={p.id} p={p} team={team} onOpen={onOpen} />)}
    </section>
  );
}

function ContestedRow({ p, team, onOpen }: { p: ContestedPair; team: boolean; onOpen: (id: string) => void }) {
  const toast = useToast();
  const [pick, setPick] = useState<string | null>(null);
  const [both, setBoth] = useState(false);
  const [note, setNote] = useState('');
  const resolve = useResearchChange((b: { keepId: string } | { keepBoth: true; note: string }) => api.research.resolve(p.id, b));
  const other = (id: string) => p.sides.find(s => s.id !== id);
  return (
    <div className="cpair" data-pair={p.id}>
      <div className="xs mut">{p.originLabel}{p.note ? `: ${p.note}` : ''}</div>
      <div className="cpsides">
        {p.sides.map(s => (
          <div className={`cpside${pick === s.id ? ' on' : ''}`} key={s.id} data-side={s.id}>
            {s.hidden ? <div className="sm mut">A finding not verified yet disagrees with this one. The team is checking it.</div> : <>
              <div className="rowc gap6" style={{ flexWrap: 'nowrap' }}><span className="catl">{s.category}</span><span style={{ flex: 1 }} /><span className={`xs vby${s.status === 'kept' ? '' : ' un'}`}>{s.byLabel}</span></div>
              <button type="button" className="flink" onClick={() => onOpen(s.id)}>{s.title}</button>
              {s.claim && <div className="sm">{s.claim}</div>}
              {s.quote && <q className="cpq">{s.quote}</q>}
              {s.sourceDomain && (s.sourceUrl && /^https?:/.test(s.sourceUrl)
                ? <a className="link xs" href={s.sourceUrl} target="_blank" rel="noreferrer noopener">{s.sourceDomain}</a> : <span className="xs mut">{s.sourceDomain}</span>)}
              {team && pick !== s.id && <button type="button" className="bt sm" style={{ alignSelf: 'flex-start' }} onClick={() => { setPick(s.id); setBoth(false); }}>This one holds up</button>}
              {team && pick === s.id && <div className="rowc gap6"><span className="xs">Keep this one and dismiss &quot;{other(s.id)?.title ?? 'the other'}&quot;?</span>
                <button type="button" className="bt pri sm" disabled={resolve.isPending} onClick={() => resolve.mutate({ keepId: s.id }, { onSuccess: () => toast('Resolved. The other one was dismissed.') })}>Resolve</button>
                <button type="button" className="bt sm" onClick={() => setPick(null)}>Cancel</button></div>}
            </>}
          </div>
        ))}
      </div>
      {team && (both
        ? <form className="rowc gap6" aria-label="Keep both" onSubmit={e => { e.preventDefault(); if (note.trim()) resolve.mutate({ keepBoth: true, note: note.trim() }, { onSuccess: () => toast('Both kept, with your note.') }); }}>
            <input className="inp" style={{ flex: 1, minWidth: 200 }} aria-label="Why both stay" placeholder="Why both stay, for example: they measure different things" value={note} onChange={e => setNote(e.target.value)} data-autofocus />
            <button className="bt pri sm" disabled={!note.trim() || resolve.isPending}>Keep both</button><button type="button" className="bt sm" onClick={() => setBoth(false)}>Cancel</button></form>
        : <div><button type="button" className="bt sm" onClick={() => { setBoth(true); setPick(null); }}>Keep both, with a note</button></div>)}
      {resolve.error ? <ErrorNote error={resolve.error} /> : null}
    </div>
  );
}

// How often to look again: every few seconds while the worker has something running, else rarely.
const working = (s: ResearchTask['status']) => s === 'queued' || s === 'running';
export const taskPoll = (q: { state: { data?: ResearchTask | undefined } }) => (q.state.data && working(q.state.data.status) ? 3_000 : 30_000);
export const tasksPoll = (q: { state: { data?: ResearchTask[] | undefined } }) => (q.state.data?.some(t => working(t.status)) ? 3_000 : 30_000);
export const followPoll = (q: { state: { data?: Follow[] | undefined } }) => (q.state.data?.some(f => !f.lastCheckedAt) ? 3_000 : 30_000);


/**
 * Listen: the words read out (ElevenLabs; a short tone in development), on the one shared audio element, unlocked in
 * the tap before the read-out is fetched (iOS plays only from an element first played in a tap). If the browser still
 * refuses, the button becomes "Tap to hear it". Stop ends it.
 */
export function ListenButton({ text, className = 'bt sm' }: { text: string; className?: string }) {
  const [state, setState] = useState<'idle' | 'loading' | 'playing' | 'blocked'>('idle');
  const url = useRef<string | null>(null);
  const [err, setErr] = useState('');
  const stop = () => { stopAudio(); setState('idle'); };
  useEffect(() => () => { if (url.current) { stopAudio(); URL.revokeObjectURL(url.current); } }, []);
  const hear = (u: string) => {
    setState('playing');
    void playAudio(u).then(r => {
      if (r === 'refused') setState('blocked');
      else { if (r === 'failed') setErr('Could not play it. Try again.'); setState(s => (s === 'playing' ? 'idle' : s)); }
    });
  };
  const play = async () => {
    unlockAudio();
    setErr(''); setState('loading');
    try {
      const r = await api.voice.speak(text);
      if (url.current) URL.revokeObjectURL(url.current);
      url.current = URL.createObjectURL(r.audio);
      hear(url.current);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not read it out.'); setState('idle'); }
  };
  return <>
    <button type="button" className={className} aria-pressed={state === 'playing'} disabled={state === 'loading'}
      onClick={() => (state === 'playing' ? stop() : state === 'blocked' && url.current ? (unlockAudio(), hear(url.current)) : void play())}>
      {state === 'playing' ? 'Stop' : state === 'loading' ? 'Starting' : state === 'blocked' ? 'Tap to hear it' : 'Listen'}</button>
    {err && <span className="xs" role="alert" style={{ color: 'var(--red-tx)' }}>{err}</span>}
  </>;
}

/** "1d 8h" until the next run. */
export function untilText(iso: string) {
  const m = Math.max(0, Math.round((Date.parse(iso) - Date.now()) / 60_000));
  const d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m % 60}m` : `${m}m`;
}

// ---------- an insight ----------

export function InsightBody({ i, team, onSource, onMore }: { i: Insight; team: boolean; onSource?: (domain: string) => void; onMore?: (title: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [marking, setMarking] = useState(false);
  const toast = useToast();
  const [confirm, setConfirm] = useState<string | null>(null);
  const decide = useResearchChange((b: { decision: 'keep' | 'dismiss' | 'undo'; anyway?: boolean }) => api.research.decide(i.id, b.decision, b.anyway));
  const keep = (anyway = false) => decide.mutate({ decision: 'keep', anyway }, {
    onSuccess: () => { setConfirm(null); toast('Kept. It is verified for the team.'); },
    onError: e => { if (e instanceof ApiError && e.code === 'check_failed') setConfirm(e.message); },
  });
  return (
    <div className="col ins" style={{ gap: 16 }} data-insight-body={i.id}>
      <div className="rowc gap6"><span className="catl">{i.category}</span>{i.status === 'new' && <span className="newb">New</span>}{confidencePill(i)}<span className={`xs vby${i.status === 'kept' ? '' : ' un'}`}>{i.byLabel}</span></div>
      <h2 style={{ margin: 0 }}>{i.title}</h2>
      <div className="xs mut">{i.originLabel} · {ago(i.createdAt)}</div>
      {!!i.tags.length && <div className="gtags" aria-label="Tags">{i.tags.map(t => <Link key={t} className="itg" to={`/knowledge?tab=ins&tag=${encodeURIComponent(t)}`}>#{t}</Link>)}</div>}
      {!!i.contradictions.length && <section className="panel col gap6 ctdi" aria-label="Contested">
        <div className="sm"><b>Contested.</b> Its sources disagree with {i.contradictions.length === 1 ? 'another insight' : `${i.contradictions.length} other insights`}. Resolve it in Contested on the Insights list.</div>
        {i.contradictions.map(c => <div key={c.id} className="xs">{c.otherTitle ? <Link className="link" to={`/knowledge?insight=${c.otherId}`}>{c.otherTitle}</Link> : <span className="mut">A finding not verified yet</span>}
          <span className="mut"> · {c.originLabel}{c.note ? `: ${c.note}` : ''}</span></div>)}
      </section>}
      {team && i.status !== 'dismissed' && !editing && <div className="rowc gap6">
        <button type="button" className="bt sm" onClick={() => setEditing(true)}>Edit</button>
        {onMore && <button type="button" className="bt sm" onClick={() => onMore(i.title)}>Research more</button>}
        {!marking && <button type="button" className="bt sm" onClick={() => setMarking(true)}>Contradicts...</button>}
        <ConfidencePicker i={i} />
      </div>}
      {marking && <ContradictsPicker i={i} onDone={() => setMarking(false)} />}
      {editing && <EditInsight i={i} onDone={() => setEditing(false)} />}
      {i.status === 'new' && team && (
        <div className="panel col gap6" role="region" aria-label="Check it">
          <div className="sm">Found by the assistant. Keep it if the source holds up; until then it is unverified, and partners do not see it.</div>
          {i.checkFailed && <div className="sm" role="alert" style={{ color: 'var(--amb-tx)' }}>{i.evidence.filter(e => e.check !== 'match').map(e => `${e.checkLabel}: "${e.claim}"`).join(' ')}</div>}
          {confirm ? <div className="rowc gap6"><span className="sm">{confirm}</span><button type="button" className="bt pri sm" disabled={decide.isPending} onClick={() => keep(true)}>Keep anyway</button><button type="button" className="bt sm" onClick={() => setConfirm(null)}>Cancel</button></div>
            : <div className="rowc gap6"><button type="button" className="bt pri" disabled={decide.isPending} onClick={() => keep()}>Keep</button><button type="button" className="bt" disabled={decide.isPending} onClick={() => decide.mutate({ decision: 'dismiss' })}>Dismiss</button></div>}
          {decide.error && !(decide.error instanceof ApiError && decide.error.code === 'check_failed') ? <ErrorNote error={decide.error} /> : null}
        </div>
      )}
      {i.status === 'dismissed' && team && <div className="rowc gap6"><span className="sm mut">Dismissed. Nothing was kept.</span><button type="button" className="bt sm" onClick={() => decide.mutate({ decision: 'undo' })}>Undo</button></div>}
      <section aria-label="Summary"><div className="k">Summary</div><p className="sm" style={{ lineHeight: 1.6 }}>{i.summary}</p></section>
      {!!i.keyPoints.length && <section aria-label="Key points"><div className="k">Key points</div><div className="col" style={{ gap: 6 }}>{i.keyPoints.map((p, n) => <div className="kp" key={n}><i />{p}</div>)}</div></section>}
      {i.details && <section aria-label="Details"><div className="k">Details</div><p className="sm" style={{ lineHeight: 1.6 }}>{i.details}</p></section>}
      <section aria-label="Sources" className="col" style={{ gap: 8 }}>
        <div className="k">Sources</div>
        {i.evidence.map((e, n) => (
          <div className="ev" key={n}>
            <b className="sm">{e.claim}</b>
            <q>{e.quote}</q>
            <div className="rowc gap6 xs">
              {/^https?:/.test(e.url) ? <a className="link" href={e.url} target="_blank" rel="noreferrer noopener">{e.domain}</a> : <span className="mut">{e.domain}</span>}
              {e.published && <span className="mut">{e.published}</span>}
              <span className={`pl ${e.check === 'match' ? 'grn' : 'amb'}`}>{e.checkLabel}</span>
            </div>
          </div>
        ))}
        {i.sourceDomain && onSource && <button type="button" className="bt sm" style={{ alignSelf: 'flex-start' }} onClick={() => onSource(i.sourceDomain!)}>More from {i.sourceDomain}</button>}
      </section>
      {i.why && <section aria-label="Why it matters"><div className="k">Why it matters</div><p className="sm">{i.why}</p>
        {!!i.tracks.length && <div className="rowc gap6">{i.tracks.map(t => <Link key={t.id} className="tchip" to={`/tracks/${t.id}`}><i />{t.spaceName} · {t.name}</Link>)}</div>}</section>}
      {!!i.related.length && <section aria-label="Related insights"><div className="k">Related</div><div className="col" style={{ gap: 6 }}>{i.related.map(r => <Link key={r.id} className="inscard" to={`/knowledge?insight=${r.id}`}><span className="catl">{r.category}</span><b>{r.title}</b></Link>)}</div></section>}
      {team && i.status !== 'dismissed' && <SaveToFolder i={i} />}
    </div>
  );
}

/** What the assistant wrote, changed by hand: the title, category, summary and why it matters. */
function EditInsight({ i, onDone }: { i: Insight; onDone: () => void }) {
  const [v, setV] = useState({ title: i.title, category: i.category, summary: i.summary, why: i.why, tags: i.tags.map(t => `#${t}`).join(' ') });
  const save = useResearchChange(() => api.research.patchInsight(i.id, { ...v, tags: splitTags(v.tags) }));
  return (
    <form className="panel col gap6" aria-label="Edit the insight" onSubmit={e => { e.preventDefault(); save.mutate(undefined, { onSuccess: onDone }); }}>
      <label className="fld">Title<input className="inp" value={v.title} onChange={e => setV({ ...v, title: e.target.value })} required /></label>
      <label className="fld">Category<select className="inp" aria-label="Category" value={v.category} onChange={e => setV({ ...v, category: e.target.value as InsightCategory })}>
        {INSIGHT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select></label>
      <label className="fld">Summary<textarea className="inp" rows={4} value={v.summary} onChange={e => setV({ ...v, summary: e.target.value })} required /></label>
      <label className="fld">Why it matters<textarea className="inp" rows={2} value={v.why} onChange={e => setV({ ...v, why: e.target.value })} /></label>
      <label className="fld">Tags<input className="inp" placeholder="#validation #imaging" value={v.tags} onChange={e => setV({ ...v, tags: e.target.value })} /></label>
      {save.error ? <ErrorNote error={save.error} /> : null}
      <div className="rowc gap6"><button className="bt pri sm" disabled={save.isPending}>Save changes</button><button type="button" className="bt sm" onClick={onDone}>Cancel</button></div>
    </form>
  );
}

/** "#validation, imaging" -> ['validation', 'imaging']. */
export const splitTags = (s: string) => [...new Set(s.split(/[\s,]+/).map(t => t.replace(/^#+/, '').trim().toLowerCase()).filter(Boolean))].slice(0, 8);

/** Confidence by hand: Worked out (from the sources), or Confirmed, Single source, Contested. */
function ConfidencePicker({ i }: { i: Insight }) {
  const set = useResearchChange((c: InsightConfidence | null) => api.research.patchInsight(i.id, { confidence: c }));
  return (
    <label className="rowc gap6 xs mut">Confidence
      <select className="inp sm" aria-label="Confidence" value={i.confidenceSet ? i.confidence : ''} disabled={set.isPending}
        onChange={e => set.mutate((e.target.value || null) as InsightConfidence | null)}>
        <option value="">From its sources{i.confidenceSet ? '' : ` (${CONFIDENCE_LABELS[i.confidence]})`}</option>
        {INSIGHT_CONFIDENCE.map(c => <option key={c} value={c}>{CONFIDENCE_LABELS[c]}</option>)}
      </select>
    </label>
  );
}

/** "Contradicts...": pick the insight this one disagrees with, say what disagrees; both become Contested. */
function ContradictsPicker({ i, onDone }: { i: Insight; onDone: () => void }) {
  const toast = useToast();
  const all = useQuery({ queryKey: ['insights'], queryFn: () => api.research.insights() });
  const [other, setOther] = useState('');
  const [note, setNote] = useState('');
  const mark = useResearchChange(() => api.research.contradicts(i.id, other, note.trim() || null));
  const choices = (all.data ?? []).filter(x => x.id !== i.id && !i.contradictions.some(c => c.otherId === x.id));
  return (
    <form className="panel col gap6" aria-label="Contradicts" onSubmit={e => { e.preventDefault(); if (other) mark.mutate(undefined, { onSuccess: () => { toast('Marked as contested. Both stay until someone resolves it.'); onDone(); } }); }}>
      <label className="fld">Contradicts<select className="inp" aria-label="Contradicts which insight" value={other} onChange={e => setOther(e.target.value)} data-autofocus>
        <option value="">Pick the insight it disagrees with</option>
        {choices.map(x => <option key={x.id} value={x.id}>{x.title}{x.status === 'new' ? ' (unverified)' : ''}</option>)}
      </select></label>
      <label className="fld">What disagrees (optional)<input className="inp" value={note} maxLength={300} placeholder="One says required, the other says recommended" onChange={e => setNote(e.target.value)} /></label>
      {mark.error ? <ErrorNote error={mark.error} /> : null}
      <div className="rowc gap6"><button className="bt pri sm" disabled={!other || mark.isPending}>Mark as contested</button><button type="button" className="bt sm" onClick={onDone}>Cancel</button></div>
    </form>
  );
}

function SaveToFolder({ i }: { i: Insight }) {
  const toast = useToast();
  const folders = useQuery({ queryKey: ['folders'], queryFn: () => api.knowledge.folders() });
  const [folderId, setFolderId] = useState('');
  const save = useResearchChange((f: string) => api.research.saveInsight(i.id, f));
  return (
    <section aria-label="Save to a folder" className="col gap6">
      <div className="k">Save to a folder</div>
      {i.savedTo.map(s => <div className="xs" key={s.fileId}>Saved to <Link className="link" to={`/folders/${s.folderId}`}>{s.folderName}</Link></div>)}
      <div className="rowc gap6">
        <select className="inp" aria-label="Folder" value={folderId} onChange={e => setFolderId(e.target.value)} style={{ maxWidth: 320 }}>
          <option value="">Pick a folder</option>
          {(folders.data ?? []).map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <button type="button" className="bt" disabled={!folderId || save.isPending} onClick={() => save.mutate(folderId, { onSuccess: () => toast('Saved to the folder. The assistant wrote its summary.') })}>Save</button>
      </div>
      <div className="xs mut">It becomes a file in that folder, with its summary; anyone who can see the folder can use it.</div>
      {save.error ? <ErrorNote error={save.error} /> : null}
    </section>
  );
}

function InsightSheet({ id, team, onClose, onSource, onMore }: { id: string; team: boolean; onClose: () => void; onSource: (d: string) => void; onMore: (title: string) => void }) {
  const q = useQuery({ queryKey: ['insight', id], queryFn: () => api.research.insight(id) });
  return (
    <Sheet crumbs={<>Research Center / Insight</>} onClose={onClose} label={q.data?.title ?? 'Insight'} width={760}>
      {q.isLoading ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : q.data ? <InsightBody i={q.data} team={team} onSource={onSource} onMore={onMore} /> : null}
    </Sheet>
  );
}

/**
 * Add an insight by hand (Kareem, 4 Nov: "Fill it in myself" should be a clean form): a link or the pasted text (one
 * of them), why it matters, and a category if you know it. The assistant reads it and writes the summary, key points
 * and details; you can edit them after. On a phone the same form goes in a bottom sheet.
 */
export function InsightForm({ onAdded, onCancel, phone = false }: { onAdded: (id: string) => void; onCancel: () => void; phone?: boolean }) {
  const [kind, setKind] = useState<'link' | 'text'>('link');
  const [v, setV] = useState({ url: '', text: '', why: '', category: '' });
  const add = useResearchChange(() => api.research.addInsight({
    ...(kind === 'link' ? { url: v.url.trim() } : { text: v.text }), why: v.why.trim() || null, category: (v.category || null) as InsightCategory | null,
  }));
  const ready = kind === 'link' ? /^https?:\/\/\S+\.\S+/i.test(v.url.trim()) : v.text.trim().length >= 20;
  const submit = (e: FormEvent) => { e.preventDefault(); if (ready) add.mutate(undefined, { onSuccess: i => onAdded(i.id) }); };
  return (
    <form className={`insform${phone ? ' phone' : ''}`} onSubmit={submit} aria-label="Add an insight">
      <div className="seg2" role="tablist" aria-label="From">
        <button type="button" role="tab" aria-selected={kind === 'link'} className={kind === 'link' ? 'on' : ''} onClick={() => setKind('link')}>A link</button>
        <button type="button" role="tab" aria-selected={kind === 'text'} className={kind === 'text' ? 'on' : ''} onClick={() => setKind('text')}>Pasted text</button>
      </div>
      {kind === 'link'
        ? <label className="fld">Link<input className="inp" type="url" inputMode="url" placeholder="https://" value={v.url} onChange={e => setV({ ...v, url: e.target.value })} data-autofocus /></label>
        : <label className="fld">The text<textarea className="inp" rows={6} placeholder="Paste the article, the report's summary, or your notes" value={v.text} onChange={e => setV({ ...v, text: e.target.value })} data-autofocus /></label>}
      <label className="fld">Why it matters (optional)<input className="inp" placeholder="What it changes for our work, or who should read it" value={v.why} onChange={e => setV({ ...v, why: e.target.value })} maxLength={500} /></label>
      <label className="fld">Category (optional)<select className="inp" value={v.category} onChange={e => setV({ ...v, category: e.target.value })}>
        <option value="">Let the assistant choose</option>
        {INSIGHT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
      </select></label>
      <div className="xs mut insnote">The assistant reads it and writes the summary, key points and details. You can edit them after.</div>
      {add.error ? <ErrorNote error={add.error} /> : null}
      <div className="remfoot">
        <span className="xs" />
        <button type="button" className={phone ? 'ph-btf ghost' : 'bt'} onClick={onCancel}>Cancel</button>
        <button className={phone ? 'ph-btf' : 'bt pri'} disabled={add.isPending || !ready}>{add.isPending ? <span className="spin" /> : null}{add.isPending ? 'Reading it' : 'Add insight'}</button>
      </div>
    </form>
  );
}

// ---------- following ----------

function FollowingTab({ team, onOpen }: { team: boolean; onOpen: (id: string) => void }) {
  const q = useQuery({ queryKey: ['follows'], queryFn: api.research.follows, refetchInterval: followPoll });
  const [f, setF] = useState({ url: '', watchFor: '', frequency: 'daily' as FollowFrequency });
  const add = useResearchChange(() => api.research.addFollow({ url: f.url.trim(), watchFor: f.watchFor || null, frequency: f.frequency }));
  return (
    <>
      {team && (
        <form className="panel folf" onSubmit={e => { e.preventDefault(); add.mutate(undefined, { onSuccess: () => setF({ url: '', watchFor: '', frequency: 'daily' }) }); }}>
          <label className="fld">Link to follow<input className="inp" type="url" placeholder="https://" value={f.url} onChange={e => setF({ ...f, url: e.target.value })} required /></label>
          <label className="fld">What to watch for<input className="inp" placeholder="New guidance on clinical AI validation" value={f.watchFor} onChange={e => setF({ ...f, watchFor: e.target.value })} /></label>
          <label className="fld">Check<select className="inp" aria-label="How often to check" value={f.frequency} onChange={e => setF({ ...f, frequency: e.target.value as FollowFrequency })}>{FOLLOW_FREQUENCIES.map(x => <option key={x} value={x}>{FREQUENCY_LABELS[x]}</option>)}</select></label>
          <button className="bt pri" disabled={add.isPending}>Follow</button>
          {add.error ? <div style={{ gridColumn: '1 / -1' }}><ErrorNote error={add.error} /></div> : null}
        </form>
      )}
      <div className="tblwrap panel" role="table" aria-label="Following">
        <div className="fl h" role="row"><span role="columnheader">Source</span><span role="columnheader">Watching for</span><span role="columnheader">Checks</span><span role="columnheader">Last checked</span><span role="columnheader">Found</span></div>
        {q.isLoading ? <Loading /> : (q.data ?? []).map(x => (
          <div className="fl" role="row" key={x.id} data-follow={x.id}>
            <div role="cell" style={{ minWidth: 0 }}><button type="button" className="flink" onClick={() => onOpen(x.id)}>{x.name}</button><div className="u">{x.url.replace(/^https?:\/\//, '')}</div></div>
            <span role="cell" className="sm mut">{x.watchFor || 'Anything new'}</span><span role="cell" className="sm">{x.frequencyLabel}</span>
            <span role="cell" className="xs mono mut">{x.lastCheckedAt ? ago(x.lastCheckedAt) : 'Not yet'}</span>
            <span role="cell" className="stt"><i style={{ background: x.lastResult?.startsWith('Could not') ? 'var(--red)' : x.foundThisMonth ? 'var(--grn)' : 'var(--line2)' }} />{x.lastResult ?? 'Waiting'}</span>
          </div>
        ))}
        {q.data && !q.data.length && <div className="sm mut" style={{ padding: 16 }}>Not following any links yet.</div>}
      </div>
    </>
  );
}

function FollowSheet({ id, team, onClose, onInsight }: { id: string; team: boolean; onClose: () => void; onInsight: (id: string) => void }) {
  const q = useQuery({ queryKey: ['follow', id], queryFn: () => api.research.follow(id), refetchInterval: 15_000 });
  const check = useResearchChange(() => api.research.checkNow(id));
  const stop = useResearchChange(() => api.research.stopFollowing(id));
  const f: Follow | undefined = q.data;
  return (
    <Sheet crumbs={<>Research Center / Following</>} onClose={onClose} label={f?.name ?? 'Followed link'} width={620}>
      {q.isLoading ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : f ? (
        <div className="col" style={{ gap: 14 }}>
          <h2 style={{ margin: 0 }}>{f.name}</h2>
          <a className="link xs" href={f.url} target="_blank" rel="noreferrer noopener">{f.url}</a>
          <div className="bl" style={{ gridTemplateColumns: '130px 1fr' }}>
            <b>Watching for</b><span>{f.watchFor || 'Anything new'}</span><b>Checks</b><span>{f.frequencyLabel} · next {ago(f.nextCheckAt)}</span>
            <b>Last result</b><span>{f.lastResult ?? 'Waiting'}</span>
          </div>
          <div className="k">What it found</div>
          {f.checks.map((c, n) => (
            <div className="lrow" key={n} style={{ alignItems: 'flex-start' }}>
              <span className="xs mono mut" style={{ width: 90, flex: 'none' }}>{ago(c.at)}</span>
              <div className="col" style={{ gap: 4, flex: 1 }}><span className="sm">{c.summary}</span>
                {c.insights.map(x => <button key={x.id} type="button" className="flink sm" onClick={() => onInsight(x.id)}>{x.title}</button>)}</div>
            </div>
          ))}
          {team && <div className="rowc gap6"><button type="button" className="bt" disabled={check.isPending} onClick={() => check.mutate(undefined)}>Check now</button>
            <button type="button" className="bt" disabled={stop.isPending} onClick={() => stop.mutate(undefined, { onSuccess: onClose })}>Stop following</button></div>}
        </div>
      ) : null}
    </Sheet>
  );
}

// ---------- research ----------

function ResearchTab({ onOpen }: { onOpen: (id: string) => void }) {
  const tasks = useQuery({ queryKey: ['research-tasks'], queryFn: api.research.tasks, refetchInterval: tasksPoll });
  const runs = useQuery({ queryKey: ['research-runs'], queryFn: api.research.runs });
  const ov = useQuery({ queryKey: ['research'], queryFn: api.research.overview });
  return (
    <div className="colsx">
      <div className="l col" style={{ gap: 14 }}>
        <section className="panel" aria-label="Research tasks">
          <div className="sh" style={{ marginBottom: 6 }}><b>Research tasks</b></div>
          <div className="xs mut" style={{ marginBottom: 6 }}>Pick a level when you start one: Quick, Standard or Deep. You can also ask in the chat, for example &quot;research how GCC hospitals validate clinical AI&quot;.</div>
          <div className="task h"><span>Question</span><span>Level</span><span>Status</span><span>Result</span></div>
          {tasks.isLoading ? <Loading /> : (tasks.data ?? []).map((t: ResearchTask) => (
            <div className="task" key={t.id} data-task={t.id}>
              <div style={{ minWidth: 0 }}><button type="button" className="flink" onClick={() => onOpen(t.id)}>{t.question}</button><div className="xs mut">{t.originLabel}</div></div>
              {levelTag(t.level)}
              <span className="stt"><i style={{ background: t.status === 'done' ? 'var(--grn)' : t.status === 'failed' || t.status === 'needs_input' ? 'var(--red)' : t.status === 'queued' ? 'var(--line2)' : 'var(--amb)' }} />{t.statusLabel}</span>
              <span className="sm mut">{t.outLabel}</span>
            </div>
          ))}
        </section>
      </div>
      <div className="r col" style={{ gap: 14 }}>
        <Topics />
        <section className="panel" aria-label="Research runs">
          <div className="sh"><b>Research runs</b><span>Every 3 days, 06:00 · Standard</span></div>
          {ov.data && <div className="xs mut">Next run in {untilText(ov.data.nextRunAt)}</div>}
          {(runs.data ?? []).map(r => (
            <div className="lrow" key={r.id} style={{ alignItems: 'flex-start' }}>
              <div className="col" style={{ gap: 2 }}><b className="sm">{new Date(r.startedAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · {r.insights.length} {r.insights.length === 1 ? 'insight' : 'insights'}</b>
                <span className="xs mut">{r.summary ?? 'Running'}</span>
                {r.insights.map(x => <Link key={x.id} className="xs link" to={`/knowledge?tab=res&insight=${x.id}`}>{x.title}</Link>)}</div>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

/** Research topics: what the runs search. Admins and the Research lead edit them. */
export function Topics() {
  const ov = useQuery({ queryKey: ['research'], queryFn: api.research.overview });
  const [text, setText] = useState('');
  const add = useResearchChange((t: string) => api.research.addTopic(t));
  const remove = useResearchChange((id: string) => api.research.removeTopic(id));
  const o = ov.data;
  if (!o) return null;
  return (
    <section className="panel col gap6" aria-label="Research topics">
      <div className="sh"><b>Research topics</b><span>{o.lead ? `Research lead: ${o.lead.name}` : 'Admins edit them'}</span></div>
      <div className="xs mut">Each run searches at most 5, the ones searched longest ago first.</div>
      {o.topics.map(t => (
        <div className="lrow" key={t.id}><span className="sm" style={{ flex: 1 }}>{t.text}</span><span className="xs mut">{t.lastRunAt ? `Searched ${ago(t.lastRunAt)}` : 'Not searched yet'}</span>
          {o.canEditTopics && <button type="button" className="bt sm" aria-label={`Remove ${t.text}`} onClick={() => remove.mutate(t.id)}>Remove</button>}</div>
      ))}
      {!o.topics.length && <div className="sm mut">No topics yet: runs search nothing until one is added.</div>}
      {o.canEditTopics && (
        <form className="rowc gap6" onSubmit={e => { e.preventDefault(); if (text.trim()) add.mutate(text.trim(), { onSuccess: () => setText('') }); }}>
          <input className="inp" style={{ flex: 1 }} aria-label="New topic" placeholder="Add a topic" value={text} onChange={e => setText(e.target.value)} />
          <button className="bt" disabled={!text.trim() || add.isPending}>Add</button>
        </form>
      )}
      {add.error ? <ErrorNote error={add.error} /> : null}
    </section>
  );
}

function TaskSheet({ id, onClose, onInsight }: { id: string; onClose: () => void; onInsight: (id: string) => void }) {
  const q = useQuery({ queryKey: ['research-task', id], queryFn: () => api.research.task(id), refetchInterval: taskPoll });
  const [answer, setAnswer] = useState('');
  const send = useResearchChange((a: string) => api.research.answerTask(id, a));
  const t = q.data;
  return (
    <Sheet crumbs={<>Research Center / Research</>} onClose={onClose} label={t?.question ?? 'Research'} width={760}>
      {q.isLoading ? <Loading /> : q.error ? <ErrorNote error={q.error} /> : t ? (
        <div className="col" style={{ gap: 14 }}>
          <div className="rowc gap6">{levelTag(t.level)}<span className="xs mut">{t.originLabel}</span></div>
          <h2 style={{ margin: 0 }}>{t.report?.title ?? t.question}</h2>
          {t.report && <div className="xs mut">Question: {t.question}</div>}
          <div className="xs mut">{t.sources} sources{t.claimsChecked ? ` · ${t.claimsChecked} claims checked` : ''} · {t.statusLabel}{t.finishedAt ? `, ${ago(t.finishedAt)}` : ''}</div>
          {t.status === 'needs_input' && (
            <form className="panel col gap6" onSubmit={e => { e.preventDefault(); if (answer.trim()) send.mutate(answer.trim()); }}>
              <b className="sm">{t.inputQuestion}</b>
              <div className="rowc gap6"><input className="inp" style={{ flex: 1 }} aria-label="Your answer" value={answer} onChange={e => setAnswer(e.target.value)} /><button className="bt pri" disabled={send.isPending}>Send</button></div>
            </form>
          )}
          {t.status === 'failed' && <div className="sm" role="alert" style={{ color: 'var(--red-tx)' }}>{t.error}</div>}
          {(t.status === 'queued' || t.status === 'running') && <div className="rowc gap6 sm mut"><span className="spin" />{t.outLabel}. {LEVEL_INFO[t.level].note}</div>}
          {t.answer && t.level === 'quick' && t.status === 'done' && <section aria-label="Short answer"><div className="k">Short answer</div><p className="sm" style={{ lineHeight: 1.6 }}>{t.answer}</p></section>}
          {!!t.links.length && <section aria-label="Links" className="col gap6"><div className="k">Links</div>{t.links.map(l => <div key={l.url} className="xs"><a className="link" href={l.url} target="_blank" rel="noreferrer noopener">{l.domain}</a> · {l.note}</div>)}</section>}
          {t.report && <>
            <section aria-label="Summary"><div className="k">Summary</div><p className="sm" style={{ lineHeight: 1.6 }}>{t.report.summary}</p></section>
            <section aria-label="Findings" className="col gap6"><div className="k">Findings</div>{t.report.findings.map((f, n) => <div className="kp" key={n}><i /><span>{f.text} <span className="xs mut">{f.support}</span></span></div>)}</section>
            <section aria-label="Where sources disagree"><div className="k">Where sources disagree</div><p className="sm">{t.report.disagreements}</p></section>
            <section aria-label="What it means"><div className="k">What it means for AI Office</div><p className="sm">{t.report.meaning}</p></section>
            <section aria-label="Report sources" className="col" style={{ gap: 4 }}><div className="k">Sources</div>
              {t.report.sources.slice(0, 8).map(s => <a key={s.url} className="link xs" href={s.url} target="_blank" rel="noreferrer noopener">{s.domain}</a>)}
              {t.report.sources.length > 8 && <span className="xs mut">and {t.report.sources.length - 8} more</span>}</section>
          </>}
          {!!t.insights.length && <section aria-label="Insights it produced" className="col gap6"><div className="k">Insights it produced</div>
            {t.insights.map(x => <button key={x.id} type="button" className={`inscard${x.status === 'new' ? ' new' : ''}`} onClick={() => onInsight(x.id)}><span className="catl">{x.category}</span><b>{x.title}</b></button>)}</section>}
        </div>
      ) : null}
    </Sheet>
  );
}

function NewTaskSheet({ initial, onClose, onStarted }: { initial: string; onClose: () => void; onStarted: (id: string) => void }) {
  const [question, setQuestion] = useState(initial);
  const [level, setLevel] = useState<ResearchLevel>('standard');
  const start = useResearchChange(() => api.research.createTask({ question: question.trim(), level }));
  const info = useMemo(() => LEVEL_INFO[level], [level]);
  return (
    <Sheet crumbs={<><span>Research Center</span><span>/</span><b>New research task</b></>} onClose={onClose} label="New research task" width={560}>
      <form className="ntask" onSubmit={e => { e.preventDefault(); start.mutate(undefined, { onSuccess: t => onStarted(t.id) }); }}>
        {/* The question is the point: a big box that fills the free height, then the level and Start, no dead space. */}
        <label className="fld grow">What should it find out?<textarea className="inp" value={question} onChange={e => setQuestion(e.target.value)} required data-autofocus
          placeholder="The question, and what you will use the answer for. For example: how have GCC hospitals validated clinical AI before go-live, and what did regulators ask for?" /></label>
        <div className="k">Level</div>
        <div className="lvls" role="radiogroup" aria-label="Level">
          {RESEARCH_LEVELS.map(l => {
            const x = LEVEL_INFO[l];
            return (
              <button key={l} type="button" role="radio" aria-checked={level === l} className={`lvl${level === l ? ' on' : ''}`} onClick={() => setLevel(l)}>
                <span className="lvh"><b>{x.name}</b>{l === 'standard' && <span className="lvdef">Default</span>}</span>
                <span className="lvl1">{x.time} · {x.sources}</span><span className="lvl2">{x.check}</span><span className="lvl2">{x.get}</span><span className="lvc">{x.cost}</span>
              </button>
            );
          })}
        </div>
        <div className="xs mut">{info.note}</div>
        {start.error ? <ErrorNote error={start.error} /> : null}
        <div><button className="bt pri" disabled={!question.trim() || start.isPending}>{info.button}</button></div>
      </form>
    </Sheet>
  );
}

export const motionSpring = spring;
